import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import type { CSSProperties } from 'react';
import type IUseAnchoredDropdown from './interfaces/IUseAnchoredDropdown';

const DROPDOWN_MIN_WIDTH = 200;
const DROPDOWN_MAX_WIDTH = 400;
const DROPDOWN_BOUNDARY_GAP = 24;
const DROPDOWN_OFFSET = 12;

// Portal-positioned dropdown anchored to a trigger button, clamped inside an optional
// boundary element. Owns only the mechanics: callers keep their own selection state.
const useAnchoredDropdown = ({ boundarySelector, onClose }: IUseAnchoredDropdown = {}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState<CSSProperties>({});

  // onClose often closes over caller state that changes between renders, so read it through
  // a ref rather than capturing it in the effect that binds the outside-click listener.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const close = useCallback(() => {
    setIsOpen((wasOpen) => {
      if (wasOpen) {
        onCloseRef.current?.();
      }
      return false;
    });
    setSearchTerm('');
  }, []);

  // Closes without committing, for callers discarding pending changes rather than saving them.
  const reset = useCallback(() => {
    setIsOpen(false);
    setSearchTerm('');
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
      return;
    }
    setIsOpen(true);
  }, [close, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (dropdownRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }

      close();
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [close, isOpen]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const boundaryElement = boundarySelector
      ? triggerRef.current.closest(boundarySelector)
      : null;
    const boundaryRect = boundaryElement?.getBoundingClientRect();
    const boundaryLeft = boundaryRect?.left ?? 0;
    const boundaryRight = boundaryRect?.right ?? window.innerWidth;
    const minLeft = boundaryLeft + DROPDOWN_BOUNDARY_GAP;
    const maxRight = boundaryRight - DROPDOWN_BOUNDARY_GAP;
    const dropdownMaxWidth = Math.min(
      DROPDOWN_MAX_WIDTH,
      Math.max(DROPDOWN_MIN_WIDTH, maxRight - minLeft),
    );
    const left = Math.min(
      Math.max(triggerRect.left, minLeft),
      Math.max(minLeft, maxRight - dropdownMaxWidth),
    );

    setPosition({
      top: triggerRect.bottom + DROPDOWN_OFFSET,
      left,
      width: 'max-content',
      minWidth: Math.max(triggerRect.width, DROPDOWN_MIN_WIDTH),
      maxWidth: dropdownMaxWidth,
    });
  }, [boundarySelector]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updatePosition();

    const handleWindowChange = () => updatePosition();

    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);

    return () => {
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [isOpen, updatePosition]);

  return {
    triggerRef,
    dropdownRef,
    isOpen,
    position,
    searchTerm,
    setSearchTerm,
    toggle,
    close,
    reset,
  };
};

export default useAnchoredDropdown;
